import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material';
import { BASE_URL } from '../components/BaseUrl';
import { getPrimaryTextColor, getSecondaryTextColor } from '../utils/colorUtils';
import { decryptToken } from '../Utlis/Encryption';
import { useSelector } from 'react-redux';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const DynamicThemeProvider = ({ children }) => {
  const [primaryColor, setPrimaryColor] = useState('#006EC2');
  const [secondaryColor, setSecondaryColor] = useState('#2C384C');
  const [companyLogo, setCompanyLogo] = useState('');
  const [loading, setLoading] = useState(true);
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null
const user = useSelector((state) => state.auth.user);
const companyId = user?.company_id; // ✅ Correct key
console.log("🏢 companyId in color:", companyId);
  // Fetch theme colors from API
  const fetchThemeColors = async () => {
    try {
      setLoading(true);
      
      // Check if user is logged in (has token)
      if (!decryptedToken) {
        // User is not logged in, use default colors and clear cache
        setPrimaryColor('#006EC2');
        setSecondaryColor('#2C384C');
        setCompanyLogo('');
        localStorage.removeItem('theme_primary');
        localStorage.removeItem('theme_secondary');
        localStorage.removeItem('company_logo');
        return;
      }
      // Replace with your actual API endpoint
      const response = await fetch(`${BASE_URL}/company-admins/get-admin-theme-setting/${companyId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${decryptedToken}`,
          'Content-Type': 'application/json',
        },
      });
    console.log("📡 Response status:", response.status);
console.log("response is",response)
      if (response.ok) {
        const data = await response.json();
        setPrimaryColor(data.data.primary_color || '#006EC2');
        setSecondaryColor(data.data.secondary_color || '#2C384C');
        setCompanyLogo(data.data.logo || '');
        
        // Store colors and logo in localStorage for persistence
        localStorage.setItem('theme_primary', data.data.primary_color || '#006EC2');
        localStorage.setItem('theme_secondary', data.data.secondary_color || '#2C384C');
        localStorage.setItem('company_logo', data.data.logo || '');
      } else {
        // Fallback to default colors or cached colors
        const cachedPrimary = localStorage.getItem('theme_primary');
        const cachedSecondary = localStorage.getItem('theme_secondary');
        const cachedLogo = localStorage.getItem('company_logo');
        
        if (cachedPrimary) setPrimaryColor(cachedPrimary);
        if (cachedSecondary) setSecondaryColor(cachedSecondary);
        if (cachedLogo) setCompanyLogo(cachedLogo);
      }
    } catch (error) {
      console.error('Error fetching theme colors:', error);
      // Use cached colors or defaults
      const cachedPrimary = localStorage.getItem('theme_primary');
      const cachedSecondary = localStorage.getItem('theme_secondary');
      const cachedLogo = localStorage.getItem('company_logo');
      
      if (cachedPrimary) setPrimaryColor(cachedPrimary);
      if (cachedSecondary) setSecondaryColor(cachedSecondary);
      if (cachedLogo) setCompanyLogo(cachedLogo);
    } finally {
      setLoading(false);
    }
  };

  // Create dynamic theme based on colors
  const theme = createTheme({
    palette: {
      primary: {
        main: primaryColor,
        text: "#8B8D97",
      },
      secondary: {
        main: secondaryColor,
        text: "#8B8D97",
      },
      tertiary: {
        main: "#2C384C",
      },
      text: {
        primary: "#8B8D97",
      },
    },
    typography: {
      h1: {
        fontFamily: "Volkhov, serif",
        fontSize: "52px",
        fontWeight: 400,
        lineHeight: 1.1,
      },
      h2: {
        fontSize: "58px",
        fontWeight: 600,
        lineHeight: 1.1,
      },
      h3: {
        fontSize: "52px",
        fontWeight: 400,
        lineHeight: 1.1,
      },
      h4: {
        fontSize: "68px",
        fontWeight: 700,
        lineHeight: 1.1,
      },
      body1: {
        fontSize: "28px",
        fontWeight: 500,
        lineHeight: 1.7,
      },
    },
  });

  useEffect(() => {
    fetchThemeColors();
  }, [decryptedToken]);

  // Function to clear theme cache (useful for logout)
  const clearThemeCache = () => {
    localStorage.removeItem('theme_primary');
    localStorage.removeItem('theme_secondary');
    localStorage.removeItem('company_logo');
    setPrimaryColor('#006EC2');
    setSecondaryColor('#2C384C');
    setCompanyLogo('');
  };

  const updateColors = (newPrimary, newSecondary, newLogo = '') => {
    setPrimaryColor(newPrimary);
    setSecondaryColor(newSecondary);
    if (newLogo) setCompanyLogo(newLogo);
    localStorage.setItem('theme_primary', newPrimary);
    localStorage.setItem('theme_secondary', newSecondary);
    if (newLogo) localStorage.setItem('company_logo', newLogo);
  };

  const value = {
    primaryColor,
    secondaryColor,
    companyLogo,
    primaryTextColor: getPrimaryTextColor(primaryColor),
    secondaryTextColor: getSecondaryTextColor(secondaryColor),
    loading,
    updateColors,
    fetchThemeColors,
    clearThemeCache,
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};
